import React, { useCallback, useEffect, useState } from 'react';
import { Mail, SlidersHorizontal, MessageSquare, Bell } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { api } from '../services/api';

function parseEmailsFromText(text: string): string[] {
  return text
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const TEMPLATE_TYPES = [
  {
    key: 'investment_received',
    label: 'Inversión recibida',
    desc: 'Se envía al inversionista cuando se registra una nueva inversión a su nombre.',
    vars: ['propertyName', 'amount'],
  },
  {
    key: 'account_statement_available',
    label: 'Estado de cuenta disponible',
    desc: 'Se envía al inversionista cuando su estado de cuenta está listo.',
    vars: ['propertyName', 'period'],
  },
  {
    key: 'document_expiring',
    label: 'Documento por vencer',
    desc: 'Se envía cuando un documento del inversionista vence en 30 o 7 días.',
    vars: ['documentTitle', 'daysLeft'],
  },
  {
    key: 'new_prospect',
    label: 'Nuevo prospecto registrado',
    desc: 'Se envía al equipo interno cuando se registra un nuevo prospecto.',
    vars: ['prospectName', 'propertyName'],
  },
  {
    key: 'investor_missing_documents_internal',
    label: 'Documentación pendiente (interno)',
    desc: 'Alerta interna semanal al equipo JNU cuando un socio tiene documentos KYC pendientes de cargar. Ya no se le envía al socio.',
    vars: ['investorName'],
  },
  {
    key: 'investor_annual_renewal_internal',
    label: 'Renovación anual (interno)',
    desc: 'Alerta interna al equipo JNU cuando una inversión está por cumplir un año. Ya no se le envía al socio.',
    vars: ['investorName', 'propertyName', 'anniversaryDate'],
  },
] as const;

type TemplateState = Record<string, { title: string; body: string }>;

export const SystemNotificationSettings: React.FC = () => {
  // Email lists
  const [maintenanceText, setMaintenanceText] = useState('');
  const [renewalText, setRenewalText] = useState('');
  const [prospectText, setProspectText] = useState('');
  const [investorText, setInvestorText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Templates
  const [templates, setTemplates] = useState<TemplateState>({});
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [templateMessage, setTemplateMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Reminder settings
  const [reminderSettings, setReminderSettings] = useState({ contractExpiryDays: 30, documentExpiryDays: 15, enabled: true });
  const [savingReminders, setSavingReminders] = useState(false);
  const [reminderMessage, setReminderMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [emails, tpls, reminders] = await Promise.all([
        api.getSystemNotificationEmails(),
        api.getNotificationTemplates(),
        api.getReminderSettings(),
      ]);
      setMaintenanceText((emails.maintenanceNotificationEmails || []).join('\n'));
      setRenewalText((emails.renewalNotificationEmails || []).join('\n'));
      setProspectText((emails.prospectNotificationEmails || []).join('\n'));
      setInvestorText((emails.investorNotificationEmails || []).join('\n'));
      setTemplates(tpls as TemplateState);
      setReminderSettings(reminders);
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.message || 'No se pudo cargar la configuración.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const saved = await api.updateSystemNotificationEmails({
        maintenanceNotificationEmails: parseEmailsFromText(maintenanceText),
        renewalNotificationEmails: parseEmailsFromText(renewalText),
        prospectNotificationEmails: parseEmailsFromText(prospectText),
        investorNotificationEmails: parseEmailsFromText(investorText),
      });
      setMaintenanceText((saved.maintenanceNotificationEmails || []).join('\n'));
      setRenewalText((saved.renewalNotificationEmails || []).join('\n'));
      setProspectText((saved.prospectNotificationEmails || []).join('\n'));
      setInvestorText((saved.investorNotificationEmails || []).join('\n'));
      setMessage({ type: 'success', text: 'Listas de correo guardadas correctamente.' });
    } catch (e: any) {
      setMessage({
        type: 'error',
        text: e?.message || 'No se pudo guardar. Revisa que cada línea sea un correo válido.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveReminders = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingReminders(true);
    setReminderMessage(null);
    try {
      const saved = await api.updateReminderSettings(reminderSettings);
      setReminderSettings(saved);
      setReminderMessage({ type: 'success', text: 'Configuración de recordatorios guardada.' });
    } catch (e: any) {
      setReminderMessage({ type: 'error', text: e?.message || 'No se pudo guardar.' });
    } finally {
      setSavingReminders(false);
    }
  };

  const handleSaveTemplates = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingTemplates(true);
    setTemplateMessage(null);
    try {
      const saved = await api.updateNotificationTemplates(templates);
      setTemplates(saved as TemplateState);
      setTemplateMessage({ type: 'success', text: 'Plantillas guardadas correctamente.' });
    } catch (e: any) {
      setTemplateMessage({ type: 'error', text: e?.message || 'No se pudieron guardar las plantillas.' });
    } finally {
      setSavingTemplates(false);
    }
  };

  const setTemplateField = (key: string, field: 'title' | 'body', value: string) => {
    setTemplates((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const inputClass =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-primary/30 focus:border-primary';

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <SlidersHorizontal className="w-7 h-7 text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuración del sistema</h1>
          <p className="text-sm text-gray-600 mt-1">
            Correos operativos y plantillas de notificaciones. Los correos de bienvenida y recuperación
            van siempre al destinatario directo.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* ── Correos de notificación ── */}
          <form onSubmit={handleSave} className="space-y-6">
            <Card title="Correos de notificación">
              <div className="space-y-6">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-800 mb-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    Solicitud de mantenimiento nueva
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Reciben aviso cuando un inquilino crea un reporte (un correo por línea).
                  </p>
                  <textarea
                    value={maintenanceText}
                    onChange={(e) => setMaintenanceText(e.target.value)}
                    rows={4}
                    className={`${inputClass} font-mono`}
                    placeholder="admin@empresa.com&#10;mantenimiento@empresa.com"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-800 mb-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    Solicitud de renovación de contrato
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Reciben aviso cuando un inquilino solicita renovación.
                  </p>
                  <textarea
                    value={renewalText}
                    onChange={(e) => setRenewalText(e.target.value)}
                    rows={4}
                    className={`${inputClass} font-mono`}
                    placeholder="legal@empresa.com"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-800 mb-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    Nuevo prospecto registrado
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Reciben aviso cuando se registra un nuevo prospecto.
                  </p>
                  <textarea
                    value={prospectText}
                    onChange={(e) => setProspectText(e.target.value)}
                    rows={4}
                    className={`${inputClass} font-mono`}
                    placeholder="administracion@jnugroup.mx&#10;rentas@jnugroup.mx"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-800 mb-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    Documentación y renovación de socios (interno)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Equipo JNU que da seguimiento a documentos KYC pendientes y renovaciones anuales de inversión. Estos avisos ya no se le envían al socio.
                  </p>
                  <textarea
                    value={investorText}
                    onChange={(e) => setInvestorText(e.target.value)}
                    rows={4}
                    className={`${inputClass} font-mono`}
                    placeholder="inversionistas@jnugroup.mx"
                  />
                </div>
              </div>
            </Card>

            {message && (
              <p className={`text-sm ${message.type === 'success' ? 'text-green-700' : 'text-red-600'}`}>
                {message.text}
              </p>
            )}

            <div className="flex flex-wrap gap-3">
              <Button type="submit" disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar listas de correo'}
              </Button>
              <Button type="button" variant="secondary" onClick={load} disabled={saving || loading}>
                Recargar
              </Button>
            </div>
          </form>

          {/* ── Plantillas de notificación ── */}
          <form onSubmit={handleSaveTemplates} className="space-y-6">
            <Card title="Plantillas de notificación">
              <p className="text-sm text-gray-500 mb-6">
                Personaliza el título y mensaje de cada notificación in-app y correo automático.
                Usa <code className="bg-gray-100 px-1 rounded text-xs font-mono">{'{{variable}}'}</code> para
                insertar datos dinámicos.
              </p>
              <div className="space-y-6">
                {TEMPLATE_TYPES.map((tpl, i) => (
                  <div key={tpl.key}>
                    {i > 0 && <div className="border-t border-gray-100 mb-6" />}
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-3.5 h-3.5 text-primary" />
                          <span className="text-sm font-semibold text-gray-800">{tpl.label}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 ml-5">{tpl.desc}</p>
                      </div>
                      {tpl.vars.length > 0 && (
                        <div className="flex flex-wrap gap-1 flex-shrink-0">
                          {tpl.vars.map((v) => (
                            <span
                              key={v}
                              className="text-xs font-mono bg-primary/8 text-primary px-1.5 py-0.5 rounded"
                            >
                              {`{{${v}}}`}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2 ml-5">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Título</label>
                        <input
                          type="text"
                          value={templates[tpl.key]?.title ?? ''}
                          onChange={(e) => setTemplateField(tpl.key, 'title', e.target.value)}
                          className={inputClass}
                          placeholder="Título de la notificación"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Mensaje</label>
                        <textarea
                          value={templates[tpl.key]?.body ?? ''}
                          onChange={(e) => setTemplateField(tpl.key, 'body', e.target.value)}
                          rows={2}
                          className={inputClass}
                          placeholder="Cuerpo del mensaje…"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {templateMessage && (
              <p className={`text-sm ${templateMessage.type === 'success' ? 'text-green-700' : 'text-red-600'}`}>
                {templateMessage.text}
              </p>
            )}

            <div className="flex flex-wrap gap-3">
              <Button type="submit" disabled={savingTemplates}>
                {savingTemplates ? 'Guardando…' : 'Guardar plantillas'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Reminder settings section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#25326B]" />
          <h2 className="text-lg font-semibold text-gray-900">Recordatorios automáticos</h2>
        </div>
        <p className="text-sm text-gray-500">
          Configura con cuántos días de anticipación se envían recordatorios por vencimiento de contratos y documentos.
        </p>
        <form onSubmit={handleSaveReminders} className="space-y-4">
          <Card className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-800">Activar recordatorios</p>
                <p className="text-xs text-gray-500 mt-0.5">Envía recordatorios automáticos por correo y notificación in-app.</p>
              </div>
              <button
                type="button"
                onClick={() => setReminderSettings(s => ({ ...s, enabled: !s.enabled }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                  reminderSettings.enabled ? 'bg-[#25326B]' : 'bg-gray-200'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  reminderSettings.enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Días antes del vencimiento de contrato
                </label>
                <p className="text-xs text-gray-500">Aviso cuando un contrato está por vencer.</p>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={reminderSettings.contractExpiryDays}
                  onChange={(e) => setReminderSettings(s => ({ ...s, contractExpiryDays: parseInt(e.target.value) || 30 }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#25326B]/30"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Días antes del vencimiento de documentos
                </label>
                <p className="text-xs text-gray-500">Aviso cuando un documento del inversionista está por vencer.</p>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={reminderSettings.documentExpiryDays}
                  onChange={(e) => setReminderSettings(s => ({ ...s, documentExpiryDays: parseInt(e.target.value) || 15 }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#25326B]/30"
                />
              </div>
            </div>
          </Card>

          {reminderMessage && (
            <p className={`text-sm ${reminderMessage.type === 'success' ? 'text-green-700' : 'text-red-600'}`}>
              {reminderMessage.text}
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <Button type="submit" disabled={savingReminders}>
              {savingReminders ? 'Guardando…' : 'Guardar recordatorios'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
